<template>
  <UserMenu v-if="store.user" />
  <div class="min-h-screen bg-[#ebebeb]">
  <div class="max-w-md mx-auto px-4 py-6">
    <h1 class="text-2xl font-bold mb-6">
      {{ isAdmin ? 'Cambia Password - Amministratore' : 'Cambia Password' }}
    </h1>
    <div v-if="successMessage" class="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
      {{ successMessage }}
    </div>

    <div v-if="errorMessage" class="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
      {{ errorMessage }}
    </div>

    <form @submit.prevent="changePassword" class="space-y-4">
      <div>
        <label for="currentPassword" class="block text-sm font-medium text-gray-700 mb-1">Password Attuale</label>
        <div class="relative">
          <input
            id="currentPassword"
            v-model="form.currentPassword"
            :type="showCurrentPassword ? 'text' : 'password'"
            required
            autocomplete="current-password"
            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            :class="{ 'border-red-500': errors.currentPassword }"
          />
          <button type="button" @click="showCurrentPassword = !showCurrentPassword" class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
            <svg v-if="showCurrentPassword" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.42-.08.65 0 1.66 1.34 3 3 3 .23 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
              />
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </button>
        </div>
        <p v-if="errors.currentPassword" class="mt-1 text-sm text-red-600">
          {{ errors.currentPassword }}
        </p>
      </div>

      <div>
        <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-1">Nuova Password</label>
        <div class="relative">
          <input
            id="newPassword"
            v-model="form.newPassword"
            :type="showNewPassword ? 'text' : 'password'"
            required
            minlength="8"
            autocomplete="new-password"
            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            :class="{ 'border-red-500': errors.newPassword }"
          />
          <button type="button" @click="showNewPassword = !showNewPassword" class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
            <svg v-if="showNewPassword" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.42-.08.65 0 1.66 1.34 3 3 3 .23 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
              />
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </button>
        </div>
        <p v-if="errors.newPassword" class="mt-1 text-sm text-red-600">
          {{ errors.newPassword }}
        </p>
      </div>

      <div>
        <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">Conferma Nuova Password</label>
        <div class="relative">
          <input
            id="confirmPassword"
            v-model="form.confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            required
            autocomplete="new-password"
            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            :class="{ 'border-red-500': errors.confirmPassword }"
          />
          <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
            <svg v-if="showConfirmPassword" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.42-.08.65 0 1.66 1.34 3 3 3 .23 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
              />
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </button>
        </div>
        <p v-if="errors.confirmPassword" class="mt-1 text-sm text-red-600">
          {{ errors.confirmPassword }}
        </p>

        <!-- Password criteria feedback -->
        <div v-if="form.newPassword" class="mt-2 space-y-1">
          <div class="flex items-center text-sm">
            <svg :class="form.newPassword.length >= 8 ? 'text-green-500' : 'text-gray-400'" class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <span :class="form.newPassword.length >= 8 ? 'text-green-700' : 'text-gray-500'">Almeno 8 caratteri</span>
          </div>

          <div class="flex items-center text-sm">
            <svg :class="hasUppercase ? 'text-green-500' : 'text-gray-400'" class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <span :class="hasUppercase ? 'text-green-700' : 'text-gray-500'">Una lettera maiuscola</span>
          </div>

          <div class="flex items-center text-sm">
            <svg :class="hasLowercase ? 'text-green-500' : 'text-gray-400'" class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <span :class="hasLowercase ? 'text-green-700' : 'text-gray-500'">Una lettera minuscola</span>
          </div>

          <div class="flex items-center text-sm">
            <svg :class="hasNumber ? 'text-green-500' : 'text-gray-400'" class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <span :class="hasNumber ? 'text-green-700' : 'text-gray-500'">Un numero</span>
          </div>

          <div class="flex items-center text-sm">
            <svg :class="hasSpecialChar ? 'text-green-500' : 'text-gray-400'" class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <span :class="hasSpecialChar ? 'text-green-700' : 'text-gray-500'">Un carattere speciale (!@#$%^&amp;*(),.?":{}|&lt;&gt;)</span>
          </div>

          <div class="flex items-center text-sm">
            <svg :class="passwordsMatch ? 'text-green-500' : 'text-gray-400'" class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <span :class="passwordsMatch ? 'text-green-700' : 'text-gray-500'">Le password corrispondono</span>
          </div>
        </div>
      </div>

      <div class="flex gap-4">
        <button
          type="submit"
          :disabled="isLoading"
          class="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="isLoading">Aggiornamento...</span>
          <span v-else>Cambia Password</span>
        </button>

        <router-link
          :to="cancelRoute"
          class="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 text-center"
        >
          Annulla
        </router-link>
      </div>
    </form>
  </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import { useFormStore } from '../stores/formStore'
import { useRouter, useRoute } from 'vue-router'
import UserMenu from '../components/UserMenu.vue'
import api from '../api'

export default {
  components: {
    UserMenu,
  },
  setup() {
    const store = useFormStore()
    const router = useRouter()
    const route = useRoute()

    // Determina se siamo in modalità admin basandoci sulla route
    const isAdmin = computed(() => {
      return route.path.startsWith('/admin/')
    })

    // Route di cancellazione appropriata
    const cancelRoute = computed(() => {
      return isAdmin.value ? '/admin/home' : '/home'
    })

    // Endpoint API unificato per cambio password
    const apiEndpoint = computed(() => {
      return '/admin/users/password'
    })

    const isLoading = ref(false)
    const successMessage = ref('')
    const errorMessage = ref('') // Stati per mostrare/nascondere le password
    const showCurrentPassword = ref(false)
    const showNewPassword = ref(false)
    const showConfirmPassword = ref(false)

    const form = reactive({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })

    const errors = reactive({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })

    // Computed properties for password validation
    const hasUppercase = computed(() => /[A-Z]/.test(form.newPassword))
    const hasLowercase = computed(() => /[a-z]/.test(form.newPassword))
    const hasNumber = computed(() => /[0-9]/.test(form.newPassword))
    const hasSpecialChar = computed(() => /[!@#$%^&*(),.?":{}|<>]/.test(form.newPassword))

    const passwordsMatch = computed(() => form.newPassword === form.confirmPassword && form.confirmPassword.length > 0)

    const isPasswordValid = computed(() => form.newPassword.length >= 8 && hasUppercase.value && hasLowercase.value && hasNumber.value && hasSpecialChar.value)

    const validateForm = () => {
      // Reset errors
      Object.keys(errors).forEach((key) => (errors[key] = ''))

      let isValid = true

      if (!form.currentPassword) {
        errors.currentPassword = 'La password attuale è richiesta'
        isValid = false
      }
      if (!form.newPassword) {
        errors.newPassword = 'La nuova password è richiesta'
        isValid = false
      } else if (!isPasswordValid.value) {
        errors.newPassword = 'La password deve soddisfare tutti i criteri di sicurezza'
        isValid = false
      }

      if (!form.confirmPassword) {
        errors.confirmPassword = 'La conferma password è richiesta'
        isValid = false
      } else if (form.newPassword !== form.confirmPassword) {
        errors.confirmPassword = 'Le password non corrispondono'
        isValid = false
      }

      if (form.currentPassword === form.newPassword) {
        errors.newPassword = 'La nuova password deve essere diversa da quella attuale'
        isValid = false
      }

      return isValid
    }

    const changePassword = async () => {
      successMessage.value = ''
      errorMessage.value = ''

      if (!validateForm()) {
        return
      }

      isLoading.value = true

      try {
        // Costruisci il payload in base al tipo di utente
        const payload = {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
          peopleId: store.user?.people_id,
        }

        console.log(`Chiamando API: ${apiEndpoint.value} per ${isAdmin.value ? 'admin' : 'user'}`)

        const response = await api.put(apiEndpoint.value, payload)

        if (response.data.success) {
          successMessage.value = isAdmin.value ? 'Password amministratore cambiata con successo!' : 'Password cambiata con successo!'

          // Reset form per sicurezza
          form.currentPassword = ''
          form.newPassword = ''
          form.confirmPassword = ''

          // Redirect alla home appropriata after 2 seconds
          setTimeout(() => {
            router.push(cancelRoute.value)
          }, 2000)
        } else {
          errorMessage.value = response.data.message || 'Errore durante il cambio password'
        }
      } catch (error) {
        console.error('Errore durante il cambio password:', error)

        if (error.response?.data?.field === 'currentPassword') {
          errors.currentPassword = isAdmin.value ? 'Password amministratore attuale non corretta' : 'Password attuale non corretta'
        } else {
          errorMessage.value = error.response?.data?.error || 'Errore di connessione. Riprova più tardi.'
        }
      } finally {
        isLoading.value = false
      }
    }
    return {
      store,
      isAdmin,
      cancelRoute,
      form,
      errors,
      isLoading,
      successMessage,
      errorMessage,
      showCurrentPassword,
      showNewPassword,
      showConfirmPassword,
      hasUppercase,
      hasLowercase,
      hasNumber,
      hasSpecialChar,
      passwordsMatch,
      isPasswordValid,
      changePassword,
    }
  },
}
</script>
